import React, { useState } from "react";
import classNames from "classnames";
import { PlaidConnectionWithAccounts } from "@common/types/plaidConnection";
import "./DisplayPlaidConnection.scss";
import { pluralize } from "@common/helpers/string";
import { CopyOnClick } from "@design-system/CopyOnClick/CopyOnClick";
import { usePlaidStatementsQualified } from "@hooks/usePlaidStatementsQualified";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import {
    useAddPlaidAccountsMutation,
    useBankConnectionRemovalMutation,
    useEnablePlaidStatementsMutation,
    useRefreshPlaidLinkMutation,
} from "@mutations/plaidConnection";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { Collapsible } from "@design-system/Collapsible/Collapsible";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { CollapsibleInfoBanner } from "@design-system/Collapsible/CollapsibleInfoBanner";
import { HStack } from "@design-system/HStack/HStack";
import { BrokenPlaidConnectionLink } from "../BrokenPlaidConnectionLink";
import { DisplayIntegrationAccount } from "../../integrationAccount/DisplayIntegrationAccount";
import { DisplayPlaidConnectionHeader } from "./DisplayPlaidConnectionHeader";
import { ExternalConnectionSettingsDropdown } from "./PlaidConnectionSettingsDropdown/ExternalConnectionSettingsDropdown";

interface Props {
    connection: PlaidConnectionWithAccounts;
    numberOfNotVisibleAccounts: number;
    idsOfFinancialAccountsConnectedToNativeIntegration: number[];
}

export const DisplayPlaidConnection: React.FC<Props> = ({
    connection,
    numberOfNotVisibleAccounts,
    idsOfFinancialAccountsConnectedToNativeIntegration,
}) => {
    const [showRemovalConfirmation, setShowRemovalConfirmation] =
        useState<boolean>(false);
    const connectionRemoval = useBankConnectionRemovalMutation(connection);
    const addAccounts = useAddPlaidAccountsMutation();
    const refreshPlaidLink = useRefreshPlaidLinkMutation(connection);
    const enableStatements = useEnablePlaidStatementsMutation(connection);
    const isPlaidStatementsEnabled = usePlaidStatementsQualified();
    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const shouldEnableStatements =
        isPlaidStatementsEnabled &&
        connection.statementsApiSupported === true &&
        !connection.statementsApiEnabled;
    const isEnableInProgress = enableStatements.isPending;
    const showEnableStatementsButton =
        shouldEnableStatements || isEnableInProgress;

    const isMigratedToNativeIntegration = connection.accounts.every(
        (integrationAccount) =>
            integrationAccount.financialAccountId &&
            idsOfFinancialAccountsConnectedToNativeIntegration.includes(
                integrationAccount.financialAccountId,
            ),
    );

    if (isMigratedToNativeIntegration) {
        return null;
    }

    return (
        <Collapsible
            data-connection={connection.id}
            className={classNames("plaid-connection", {
                "plaid-connection--has-error": !!connection.error,
            })}
            actions={
                <CaptureClicks>
                    <ExternalConnectionSettingsDropdown
                        connection={connection}
                        onRemove={() => setShowRemovalConfirmation(true)}
                        busy={addAccounts.isPending}
                    />
                </CaptureClicks>
            }
            header={<DisplayPlaidConnectionHeader connection={connection} />}
            footer={
                <HStack justify="between" align="center">
                    <HStack gap={12}>
                        <ButtonWithLoader
                            loading={addAccounts.isPending}
                            variant="primary"
                            size="md"
                            onClick={() => {
                                if (refuseIfViewOnly()) {
                                    return;
                                }
                                addAccounts.mutate(connection);
                            }}
                        >
                            Add another account
                        </ButtonWithLoader>
                        {connection.error ? (
                            <BrokenPlaidConnectionLink
                                connection={connection}
                                triggerContent="Fix connection"
                                triggerVariant="primary"
                                triggerClassName=""
                                triggerSize="md"
                                triggerLeftIcon={null}
                            />
                        ) : (
                            isPlaidStatementsEnabled && (
                                <ButtonWithLoader
                                    loading={
                                        showEnableStatementsButton
                                            ? enableStatements.isPending
                                            : refreshPlaidLink.isPending
                                    }
                                    variant="primary"
                                    size="md"
                                    onClick={() => {
                                        if (refuseIfViewOnly()) {
                                            return;
                                        }
                                        if (showEnableStatementsButton) {
                                            enableStatements.mutate();
                                            return;
                                        }
                                        refreshPlaidLink.mutate();
                                    }}
                                >
                                    {showEnableStatementsButton
                                        ? "Enable in Plaid"
                                        : "Refresh connection"}
                                </ButtonWithLoader>
                            )
                        )}
                    </HStack>
                    <span className="plaid-connection__id">
                        Connection id:{" "}
                        <CopyOnClick className="plaid-connection__id-copy">
                            {connection.connectionExternalId}
                        </CopyOnClick>
                    </span>
                </HStack>
            }
            defaultOpen={connection.accounts.length === 1}
            collapseButtonSize="sm"
        >
            <>
                {numberOfNotVisibleAccounts > 0 && (
                    <CollapsibleInfoBanner>
                        {numberOfNotVisibleAccounts}{" "}
                        {pluralize("account", numberOfNotVisibleAccounts)}{" "}
                        connected to entities you don't have access to.
                    </CollapsibleInfoBanner>
                )}
                {connection.accounts.map((account) => (
                    <DisplayIntegrationAccount
                        key={account.id}
                        connection={connection}
                        integrationAccount={account}
                    />
                ))}

                <ConfirmationModal
                    show={showRemovalConfirmation}
                    onHide={() => setShowRemovalConfirmation(false)}
                    title="Remove from Kick?"
                    question={
                        <>
                            This action will hide all{" "}
                            <strong>{connection.bankName}</strong> accounts from
                            Kick and prevent Kick from searching for tax
                            deductions or cash back savings.
                            <br />
                            <br />
                            Do you wish to continue?
                        </>
                    }
                    onConfirmMutation={connectionRemoval}
                    onReject={() => setShowRemovalConfirmation(false)}
                    yes="Confirm"
                    no="Cancel"
                />
            </>
        </Collapsible>
    );
};
